import type { Dispatch, SetStateAction } from 'react';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { InputTextType } from 'src/components/atoms/InputText';
import { Row } from 'src/components/atoms/Row';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { Dropdown } from 'src/components/molecules/Dropdown';
import Modal, { ModalVariant } from 'src/components/organisms/Modal';
import { actions } from 'src/features/entries/entriesSlice';
import { useManualOverride } from 'src/features/incomeVerification/hooks';
import { ManualOverrideRequest } from 'src/features/incomeVerification/ts/interfaces';
import { useAppDispatch } from 'src/store';
import { OVERRIDE_REASON_OPTIONS } from './constants';
import { StyledInputText } from './styles';

type OverrideIncomeAssetsModalProps = {
  entryId?: string;
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
};

type OverrideFormValues = {
  incomeAmount: string;
  incomeReason: string;
  assetAmount: string;
  assetReason: string;
};

const defaultValues: OverrideFormValues = {
  incomeAmount: '',
  incomeReason: '',
  assetAmount: '',
  assetReason: ''
};

const OverrideIncomeAssetsModal: FC<OverrideIncomeAssetsModalProps> = ({
  entryId,
  showModal,
  setShowModal
}) => {
  const dispatch = useAppDispatch();
  const { onManualOverride, isOverrideLoading } = useManualOverride();
  const [formError, setFormError] = useState<string | null>(null);

  const methods = useForm<OverrideFormValues>({
    mode: 'onChange',
    defaultValues
  });

  useEffect(() => {
    if (!showModal) {
      methods.reset(defaultValues);
      methods.clearErrors();
      setFormError(null);
    }
  }, [methods, showModal]);

  const handleSubmit = async (values: OverrideFormValues) => {
    setFormError(null);
    methods.clearErrors();

    const { incomeAmount, incomeReason, assetAmount, assetReason } = values;

    const hasIncomeAmount = incomeAmount !== '';
    const hasIncomeReason = incomeReason !== '';
    const hasAssetAmount = assetAmount !== '';
    const hasAssetReason = assetReason !== '';

    if (!hasIncomeAmount && !hasAssetAmount) {
      setFormError('Enter a new income or asset amount to continue.');
      return;
    }

    let hasError = false;

    if (hasIncomeAmount && !hasIncomeReason) {
      methods.setError('incomeReason', {
        type: 'manual',
        message: 'Select a reason'
      });
      hasError = true;
    }

    if (hasIncomeReason && !hasIncomeAmount) {
      methods.setError('incomeAmount', {
        type: 'manual',
        message: 'Enter an amount'
      });
      hasError = true;
    }

    if (hasAssetAmount && !hasAssetReason) {
      methods.setError('assetReason', {
        type: 'manual',
        message: 'Select a reason'
      });
      hasError = true;
    }

    if (hasAssetReason && !hasAssetAmount) {
      methods.setError('assetAmount', {
        type: 'manual',
        message: 'Enter an amount'
      });
      hasError = true;
    }

    if (hasError) {
      return;
    }

    const payload: ManualOverrideRequest = {};

    if (hasIncomeAmount && hasIncomeReason) {
      const parsedIncomeAmount = Number(incomeAmount);

      if (Number.isNaN(parsedIncomeAmount) || parsedIncomeAmount < 0) {
        methods.setError('incomeAmount', {
          type: 'manual',
          message: 'Enter a valid positive amount'
        });
        return;
      }

      payload.income_override = {
        monthly_gross_income: parsedIncomeAmount,
        override_reason_code: Number(incomeReason)
      };
    }

    if (hasAssetAmount && hasAssetReason) {
      const parsedAssetAmount = Number(assetAmount);

      if (Number.isNaN(parsedAssetAmount) || parsedAssetAmount < 0) {
        methods.setError('assetAmount', {
          type: 'manual',
          message: 'Enter a valid positive amount'
        });
        return;
      }

      payload.asset_override = {
        total_verified_assets: parsedAssetAmount,
        override_reason_code: Number(assetReason)
      };
    }

    const wasSuccessful = await onManualOverride(entryId, payload);

    if (wasSuccessful) {
      if (entryId && hasIncomeAmount) {
        dispatch(
          actions.updateEntryIncome({
            entryId,
            incomeValue: Number(incomeAmount)
          })
        );
      }
      setShowModal(false);
      methods.reset(defaultValues);
    }
  };

  return (
    <Modal
      title="Manually Override Income and Assets"
      labelButtonConfirm="Confirm & Send Report"
      labelButtonCancel="Cancel"
      showModal={showModal}
      setShowModal={setShowModal}
      onConfirm={methods.handleSubmit(handleSubmit)}
      onCancel={() => setFormError(null)}
      isLoadingButtonConfirm={isOverrideLoading}
      isDisabledButtonConfirm={isOverrideLoading}
      showCloseButton
      variant={ModalVariant.none}
      data-testid="override-income-assets-modal"
    >
      <FormProvider {...methods}>
        <Row direction="column" gap={1.5}>
          <Row.Col>
            <Text color={TextColor.gray600} variant={TextVariant.normal}>
              Enter new values for income and assets. Click confirm to send the new report.
            </Text>
          </Row.Col>

          <Row.Col>
            <Row direction="column" gap={1.5}>
              <Row.Col>
                <Text variant={TextVariant.h4}>Income Amount</Text>
              </Row.Col>
              <Row.Col>
                <Row gap={1}>
                  <Row.Col size={1}>
                    <StyledInputText
                      name="incomeAmount"
                      placeholder="$0.00"
                      type={InputTextType.number}
                      showOptionalLabel={false}
                      showLabel={false}
                      data-testid="override-income-amount"
                    />
                  </Row.Col>
                  <Row.Col size={1}>
                    <Dropdown
                      name="incomeReason"
                      placeholder="Reason"
                      data={OVERRIDE_REASON_OPTIONS}
                      data-testid="override-income-reason"
                    />
                  </Row.Col>
                </Row>
              </Row.Col>
            </Row>
          </Row.Col>

          <Row.Col>
            <Row direction="column" gap={1.5}>
              <Row.Col>
                <Text variant={TextVariant.h4}>Asset Amount</Text>
              </Row.Col>
              <Row.Col>
                <Row gap={1}>
                  <Row.Col size={1}>
                    <StyledInputText
                      name="assetAmount"
                      placeholder="$0.00"
                      type={InputTextType.number}
                      showOptionalLabel={false}
                      showLabel={false}
                      data-testid="override-asset-amount"
                    />
                  </Row.Col>
                  <Row.Col size={1}>
                    <Dropdown
                      name="assetReason"
                      placeholder="Reason"
                      data={OVERRIDE_REASON_OPTIONS}
                      data-testid="override-asset-reason"
                    />
                  </Row.Col>
                </Row>
              </Row.Col>
            </Row>
          </Row.Col>

          {formError && (
            <Row.Col>
              <Text color={TextColor.error} variant={TextVariant.small}>
                {formError}
              </Text>
            </Row.Col>
          )}
        </Row>
      </FormProvider>
    </Modal>
  );
};

export default OverrideIncomeAssetsModal;
